import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Save, Archive, History, ScrollText } from "lucide-react";
import { api, errText, btnDark, btnLight, inputCls, labelCls, fmtDateTime, utcHHMMToLocal, localHHMMToUtc } from "./api";
import { CompanyList } from "./CompanyList";

export function SettingsDrawer({ open, onClose, settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [startLocal, setStartLocal] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(settings); setStartLocal(utcHHMMToLocal(settings?.daily_start_utc)); }, [settings, open]);
  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    const cap = Number(form.daily_cap);
    if (!(cap >= 50 && cap <= 2000)) { toast.error("Daily limit must be between 50 and 2000."); return; }
    setBusy(true);
    try {
      const saved = await api.saveSettings({ ...form, daily_cap: cap, gap_min_s: Number(form.gap_min_s), gap_max_s: Number(form.gap_max_s), daily_start_utc: localHHMMToUtc(startLocal) });
      toast.success("Settings saved");
      onSaved(saved);
      onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#0F1825]/40" data-testid="settings-drawer" onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-[#FAF7F1] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl text-[#0F1825]">Signature & sending settings</h2>
          <button type="button" onClick={onClose} className="text-[#A09680] hover:text-[#0F1825]" data-testid="settings-close"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Sender name</label><input value={form.sender_name || ""} onChange={set("sender_name")} className={inputCls} placeholder="Amit D." data-testid="settings-sender-name" /></div>
            <div><label className={labelCls}>Sender title</label><input value={form.sender_title || ""} onChange={set("sender_title")} className={inputCls} placeholder="Founder, Voice2Evidence · Marksman Healthcare" data-testid="settings-sender-title" /></div>
            <div className="col-span-2"><label className={labelCls}>Key link (mandatory in every email)</label><input value={form.key_link || ""} onChange={set("key_link")} className={inputCls} placeholder="https://voice2evidence.com/deck" data-testid="settings-key-link" /><p className="mt-1 text-[11px] text-[#5C6470]">Default is the briefing deck. Drafts are asked to include it; if a draft or your signature still lacks it, one line is appended before the signature at send time.</p></div>
          </div>
          <div>
            <label className={labelCls}>Email signature · HTML (links allowed) · appended to every send</label>
            <textarea value={form.signature_html || ""} onChange={set("signature_html")} rows={7} className={`${inputCls} font-mono text-[12px] resize-y`} placeholder={'<p><strong>Amit D.</strong><br/>Founder, Voice2Evidence<br/><a href="https://voice2evidence.com">voice2evidence.com</a> · +91 …</p>'} data-testid="settings-signature" />
            <p className="mt-1 text-[11.5px] text-[#5C6470]">Leave empty to use a default built from the sender name, title and your connected mailbox.</p>
            {form.signature_html && (
              <div className="mt-2 bg-white border border-[#E8E1D2] p-3 text-[13px]" data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: /<(p|div|br|a|span|strong|b|img|table)\b/i.test(form.signature_html || "") ? form.signature_html : (form.signature_html || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br/>") }} />
            )}
          </div>
          <div>
            <label className={labelCls}>Compliance opt-out footer · {"{company}"} is replaced per contact</label>
            <textarea value={form.footer_text || ""} onChange={set("footer_text")} rows={3} className={`${inputCls} resize-y`} data-testid="settings-footer" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Daily sending limit · 50–2000</label>
              <input type="number" min={50} max={2000} value={form.daily_cap} onChange={set("daily_cap")} className={inputCls} data-testid="settings-cap" />
              <p className="mt-1 text-[11px] text-[#5C6470]">Counts every attempt (sent, failed and provider-refused). Resets at 00:00 UTC.</p>
            </div>
            <div>
              <label className={labelCls}>Daily start time · your local time</label>
              <div className="flex gap-2">
                <input type="time" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} className={inputCls} data-testid="settings-start-time" />
                {startLocal && <button type="button" onClick={() => setStartLocal("")} className={btnLight} data-testid="settings-start-clear">Clear</button>}
              </div>
              <p className="mt-1 text-[11px] text-[#5C6470]">{startLocal ? `Stored as ${localHHMMToUtc(startLocal)} UTC — no email goes out before this time each day.` : "Empty = sending may start any time of day."}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Min gap (s)</label><input type="number" min={5} max={600} value={form.gap_min_s} onChange={set("gap_min_s")} className={inputCls} data-testid="settings-gap-min" /></div>
            <div><label className={labelCls}>Max gap (s)</label><input type="number" min={5} max={600} value={form.gap_max_s} onChange={set("gap_max_s")} className={inputCls} data-testid="settings-gap-max" /></div>
          </div>
          <button type="button" onClick={save} disabled={busy} className={btnDark} data-testid="settings-save">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save settings
          </button>
        </div>
      </div>
    </div>
  );
}

export function ArchiveDialog({ open, onClose, defaultName, onArchived }) {
  const [name, setName] = useState(defaultName || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(defaultName || ""); }, [defaultName, open]);
  if (!open) return null;
  const go = async () => {
    setBusy(true);
    try { const r = await api.archive(name); toast.success(`Archived “${r.archived.name}” — board cleared for a new campaign`); onArchived(); onClose(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F1825]/40 p-4" onClick={onClose} data-testid="archive-dialog">
      <div className="bg-white w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-serif text-2xl text-[#0F1825] mb-2">Archive this campaign</h2>
        <p className="text-[13px] text-[#5C6470] mb-4">The current contacts, drafts and stats are frozen read-only under this name. The board is cleared for a new upload and a fresh campaign context. Replies keep being tracked.</p>
        <label className={labelCls}>Campaign name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="archive-name" />
        <div className="mt-4 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className={btnLight}>Cancel</button>
          <button type="button" onClick={go} disabled={busy || !name.trim()} className={btnDark} data-testid="archive-confirm">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />} Archive & start new
          </button>
        </div>
      </div>
    </div>
  );
}

export function CampaignHistory({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.campaigns().then((d) => setItems(d.campaigns)).catch(() => {}); }, [refreshKey]);
  const openCampaign = async (id, page = 1) => {
    if (openId === id && page === 1 && detail) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    try { setDetail(await api.campaign(id, page)); } catch (e) { toast.error(errText(e)); }
  };
  if (!items.length) return null;
  return (
    <section className="bg-white border border-[#E8E1D2] p-5" data-testid="campaign-history">
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-3 inline-flex items-center gap-2"><History size={12} /> Campaign history · {items.length}</div>
      <ul className="divide-y divide-[#F0EBDC]">
        {items.map((c) => (
          <li key={c.id} className="py-2.5">
            <button type="button" onClick={() => openCampaign(c.id)} className="w-full text-left flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid={`campaign-row-${c.id}`}>
              <span className="font-serif text-[1.05rem] text-[#0F1825]">{c.name}</span>
              <span className="font-mono text-[10px] text-[#A09680]">archived {fmtDateTime(c.archived_at)}</span>
              <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[#5C6470] ml-auto">
                {c.counts.total} contacts · {c.counts.sent} sent · <span className="text-[#4E378A]">{c.counts.replied} replied</span> · {c.counts.bounced} bounced
              </span>
            </button>
            {openId === c.id && (
              <div className="mt-3 pl-2 border-l-2 border-[#E8E1D2]" data-testid={`campaign-viewer-${c.id}`}>
                {detail ? (
                  <>
                    {c.context && <p className="text-[12.5px] text-[#5C6470] mb-3"><span className="font-mono text-[9.5px] uppercase tracking-[0.16em] mr-2">Context</span>{c.context}</p>}
                    <CompanyList data={detail} q="" onQ={() => {}} onChanged={() => {}} readOnly onPage={(pg) => openCampaign(c.id, pg)} />
                  </>
                ) : <div className="text-[12px] text-[#5C6470] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AuditLog({ refreshKey }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  useEffect(() => { if (open) api.audit(150).then((d) => setEvents(d.events)).catch(() => {}); }, [open, refreshKey]);
  return (
    <section className="bg-white border border-[#E8E1D2]" data-testid="audit-log">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full px-5 py-3 text-left font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] inline-flex items-center gap-2" data-testid="audit-toggle">
        <ScrollText size={12} /> Audit log {open ? "· hide" : "· show"}
      </button>
      {open && (
        <ul className="px-5 pb-4 max-h-[360px] overflow-y-auto divide-y divide-[#F0EBDC]">
          {events.map((e) => (
            <li key={e.id} className="py-1.5 flex flex-wrap gap-x-3 text-[12px]">
              <span className="font-mono text-[10px] text-[#A09680] w-[120px] shrink-0">{fmtDateTime(e.at)}</span>
              <span className={`font-mono text-[10px] uppercase tracking-[0.14em] w-[120px] shrink-0 ${e.type === "sent" ? "text-[#2E4A38]" : e.type === "blocked" || e.type === "bounced" ? "text-[#C84C21]" : "text-[#0F1825]"}`}>{e.type}</span>
              <span className="text-[#0F1825] break-all">{e.email || ""}{e.company ? ` · ${e.company}` : ""}</span>
              {e.details && <span className="text-[#5C6470] break-all">{typeof e.details === "string" ? e.details : Object.entries(e.details).filter(([k]) => !["invalid_samples", "sheets"].includes(k)).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</span>}
            </li>
          ))}
          {!events.length && <li className="py-2 text-[12px] text-[#5C6470]">No events yet.</li>}
        </ul>
      )}
    </section>
  );
}
